import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { isBrowserOnline, subscribeConnectivity } from "../../../shared/lib/connectivity";
import {
  OFFLINE_QUEUE_CHANGED_EVENT,
  countPendingOfflineSales,
  listOfflineSales,
  syncPendingOfflineSales,
} from "../lib/offlineSaleQueue";

const SYNC_INTERVAL_MS = 60 * 1000;

/**
 * Keeps the offline sale queue draining whenever the browser is online.
 * Also exposes pending count for POS UI badges.
 */
export function useOfflineSaleSync({ enabled = true, t } = {}) {
  const [online, setOnline] = useState(() => isBrowserOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const mountedRef = useRef(true);
  const tRef = useRef(t);
  tRef.current = t;

  const refreshCount = useCallback(async () => {
    try {
      const n = await countPendingOfflineSales();
      if (mountedRef.current) setPendingCount(n);
      return n;
    } catch {
      if (mountedRef.current) setPendingCount(0);
      return 0;
    }
  }, []);

  const runSync = useCallback(async ({ silent = true } = {}) => {
    if (!enabled || !isBrowserOnline()) {
      await refreshCount();
      return { synced: 0, failed: 0, remaining: 0 };
    }

    if (mountedRef.current) setSyncing(true);
    try {
      const result = await syncPendingOfflineSales();
      await refreshCount();
      const translate = tRef.current;
      if (!silent && result.synced > 0) {
        if (typeof translate === "function") {
          message.success(
            translate("posOfflineSynced", "Synced {{count}} offline sale(s)", { count: result.synced })
          );
        } else {
          message.success(`Synced ${result.synced} offline sale(s)`);
        }
      }
      return result;
    } catch {
      await refreshCount();
      return { synced: 0, failed: 0, remaining: 0 };
    } finally {
      if (mountedRef.current) setSyncing(false);
    }
  }, [enabled, refreshCount]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    refreshCount();
    runSync({ silent: true });

    const unsub = subscribeConnectivity((isOnline) => {
      setOnline(isOnline);
      if (isOnline) {
        runSync({ silent: false });
      }
    });

    const onQueueChanged = () => {
      refreshCount();
    };
    window.addEventListener(OFFLINE_QUEUE_CHANGED_EVENT, onQueueChanged);

    const intervalId = setInterval(() => {
      if (isBrowserOnline()) runSync({ silent: true });
      else refreshCount();
    }, SYNC_INTERVAL_MS);

    return () => {
      unsub();
      window.removeEventListener(OFFLINE_QUEUE_CHANGED_EVENT, onQueueChanged);
      clearInterval(intervalId);
    };
  }, [enabled, refreshCount, runSync]);

  return {
    online,
    pendingCount,
    syncing,
    refreshCount,
    runSync,
    listOfflineSales,
  };
}

export default useOfflineSaleSync;
